import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Alert,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Pets,
  Agriculture,
  LocalDining,
  Build,
  Medication,
  Category,
  Phone,
  Email,
  LocationOn,
  Visibility,
  Schedule,
  AttachMoney,
  Share,
  Favorite,
  FavoriteBorder,
  WhatsApp,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { SalesLot, ItemType, lotStatusColors, lotStatusLabels, itemTypeLabels } from '../types/marketplaceTypes';
import { marketplaceService } from '../services/marketplaceService';

interface SalesLotViewProps {
  lotId: string;
}

const getItemTypeIcon = (itemType: ItemType) => {
  const iconMap = {
    animal: <Pets />,
    fertilizer: <Agriculture />,
    nutrition: <LocalDining />,
    equipment: <Build />,
    feed: <LocalDining />,
    medicine: <Medication />,
    other: <Category />
  };
  return iconMap[itemType] || <Category />;
};

export const SalesLotView: React.FC<SalesLotViewProps> = ({ lotId }) => {
  const [lot, setLot] = useState<SalesLot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadLot();
  }, [lotId]);

  const loadLot = async () => {
    try {
      setLoading(true);
      setError(null);
      const lotData = await marketplaceService.getLotById(lotId);
      
      if (!lotData) {
        setError('Lote não encontrado');
        return;
      }
      
      setLot(lotData);
    } catch (err) {
      setError('Erro ao carregar o lote');
      console.error('Error loading lot:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = (method: 'phone' | 'email' | 'whatsapp') => {
    if (!lot?.contactInfo) return;

    switch (method) {
      case 'phone':
        if (lot.contactInfo.phone) {
          window.open(`tel:${lot.contactInfo.phone}`);
        }
        break;
      case 'email':
        if (lot.contactInfo.email) {
          window.open(`mailto:${lot.contactInfo.email}?subject=Interesse no ${lot.title}`);
        }
        break;
      case 'whatsapp':
        if (lot.contactInfo.phone) {
          const phone = lot.contactInfo.phone.replace(/\D/g, '');
          const message = encodeURIComponent(`Olá! Tenho interesse no ${lot.title}`);
          window.open(`https://wa.me/55${phone}?text=${message}`);
        }
        break;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'BRL') {
      return `R$ ${amount.toLocaleString('pt-BR')}`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={100} />
        </Box>
      </Box>
    );
  }

  if (error || !lot) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Lote não encontrado'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0C5240 0%, #6BC43C 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              mr: 2,
              width: 56,
              height: 56,
            }}
          >
            {getItemTypeIcon(lot.itemType)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {lot.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {lot.organizationName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              sx={{ color: 'white' }}
              onClick={() => setIsFavorite(!isFavorite)}
            >
              {isFavorite ? <Favorite /> : <FavoriteBorder />}
            </IconButton>
            <IconButton sx={{ color: 'white' }}>
              <Share />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={lotStatusLabels[lot.status]}
            sx={{
              bgcolor: lotStatusColors[lot.status],
              color: 'white',
              fontWeight: 600,
            }}
          />
          <Chip
            label={itemTypeLabels[lot.itemType]}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.5)' }}
          />
          {lot.isUrgent && (
            <Chip
              label="URGENTE"
              sx={{
                bgcolor: '#FF5722',
                color: 'white',
                fontWeight: 600,
                animation: 'pulse 2s infinite',
              }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Price and Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <Card sx={{ bgcolor: DESIGN_TOKENS.colors.background.elevated, border: `2px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, color: DESIGN_TOKENS.colors.primary[700] }}>
                Preço por Item
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.success[500] }}>
                {formatCurrency(lot.pricePerItem, lot.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: {formatCurrency(lot.totalValue, lot.currency)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: DESIGN_TOKENS.colors.background.elevated, border: `2px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, color: DESIGN_TOKENS.colors.primary[700] }}>
                Quantidade
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.secondary }}>
                {lot.items.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {lot.items.length === 1 ? 'item' : 'itens'} disponível{lot.items.length === 1 ? '' : 'eis'}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Description */}
        {lot.description && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: DESIGN_TOKENS.colors.background.elevated }}>
            <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.primary[700] }}>
              Descrição
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {lot.description}
            </Typography>
          </Paper>
        )}

        {/* Items List */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.primary[700] }}>
            Itens do Lote
          </Typography>
          <List>
            {lot.items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    {getItemTypeIcon(lot.itemType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {item.identifier}
                        </Typography>
                        {item.condition && (
                          <Chip size="small" label={item.condition} variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {item.description && (
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          {item.weight && (
                            <Typography variant="caption" color="text.secondary">
                              Peso: {item.weight}kg
                            </Typography>
                          )}
                          {item.age && (
                            <Typography variant="caption" color="text.secondary">
                              Idade: {item.age} meses
                            </Typography>
                          )}
                          {item.breed && (
                            <Typography variant="caption" color="text.secondary">
                              Raça: {item.breed}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < lot.items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Contact Information */}
        {lot.contactInfo && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.primary[700] }}>
              Informações de Contato
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              {lot.contactInfo.phone && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Phone />}
                  onClick={() => handleContact('phone')}
                  sx={{ justifyContent: 'flex-start', p: 2 }}
                >
                  <Box sx={{ textAlign: 'left', ml: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Telefone
                    </Typography>
                    <Typography variant="body1">
                      {lot.contactInfo.phone}
                    </Typography>
                  </Box>
                </Button>
              )}
              {lot.contactInfo.email && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() => handleContact('email')}
                  sx={{ justifyContent: 'flex-start', p: 2 }}
                >
                  <Box sx={{ textAlign: 'left', ml: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {lot.contactInfo.email}
                    </Typography>
                  </Box>
                </Button>
              )}
            </Box>
            {lot.contactInfo.phone && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<WhatsApp />}
                onClick={() => handleContact('whatsapp')}
                sx={{
                  bgcolor: '#25D366',
                  '&:hover': { bgcolor: '#1DA851' },
                  p: 2,
                }}
              >
                Entrar em contato via WhatsApp
              </Button>
            )}
          </Paper>
        )}

        {/* Additional Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.primary[700] }}>
            Informações Adicionais
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {lot.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Localização
                  </Typography>
                  <Typography variant="body1">{lot.location}</Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Visualizações
                </Typography>
                <Typography variant="body1">{lot.viewCount}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Publicado em
                </Typography>
                <Typography variant="body1">{formatDate(lot.createdAt)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ID do Lote
                </Typography>
                <Typography variant="body1">{lot.id}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Tags */}
        {lot.tags.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.primary[700] }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {lot.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: DESIGN_TOKENS.colors.background.elevated }}
                />
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};